import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';

// Components
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop';
import Safarbot from './components/Safarbot/Safarbot';
import CookieConsent from './components/CookieConsent/CookieConsent';
import GlobalSEO from './components/GlobalSEO';
import AnalyticsIntegration from './components/AnalyticsIntegration';
import SEOOptimizations from './components/SEOOptimizations';
import PerformanceOptimizations from './components/PerformanceOptimizations';
import ImageOptimization from './components/ImageOptimization';
import ResourceOptimization from './components/ResourceOptimization';
import ConsoleErrorFix from './components/ConsoleErrorFix';
import ImageProtection from './components/ImageProtection';
import WebVitalsTracker from './components/WebVitalsTracker';

// Pages
import Home from './pages/Home/Home';
import Places from './pages/Places/Places';
import PlaceDetail from './pages/PlaceDetail/PlaceDetail';
import Checklist from './pages/Checklist/Checklist';
import RouteMap from './pages/RouteMap/RouteMap';
import Blog from './pages/Blog/Blog';
import BlogPost from './pages/BlogPost/BlogPost';
import About from './pages/About/About';
import PlanTrip from './pages/PlanTrip/PlanTrip';
import FAQ from './pages/FAQ/FAQ';
import Privacy from './pages/Privacy/Privacy';
import Terms from './pages/Terms/Terms';
import AdminTrips from './pages/AdminTrips/AdminTrips';
import Admin from './pages/Admin/Admin';
import Hotels from './pages/Hotels/Hotels';
import HotelDetail from './pages/HotelDetail/HotelDetail';
import ImageProtectionTest from './pages/ImageProtectionTest/ImageProtectionTest';
import NotFound from './pages/NotFound/NotFound';

// Simple error boundary for admin route
class AdminErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state = { hasError:false, error:null }; }
  static getDerivedStateFromError(error){ return { hasError:true, error }; }
  componentDidCatch(error, info){ console.error('[AdminErrorBoundary] Error:', error, info); }
  render(){
    if (this.state.hasError) {
      return <div style={{padding:'2rem'}}><h2>Admin Panel Error</h2><p>{String(this.state.error)}</p><p>Reload or clear site cache.</p></div>;
    }
    return this.props.children;
  }
}

// Scroll reveal effect
function useScrollReveal() {
  const location = useLocation();

  useEffect(() => {
    let cleanup = () => {};

    // Small delay to ensure DOM is ready after route change
    const timer = setTimeout(() => {
      const elements = [...document.querySelectorAll('.scroll-reveal, .scroll-scale')];
      let pending = elements.filter((el) => !el.classList.contains('revealed'));

      // Synchronous on purpose. requestAnimationFrame never fires in a background
      // tab, which would leave the whole page at zero opacity, and an
      // IntersectionObserver misses elements that a fast or restored scroll
      // skips past. Reading ~15 rects on scroll is cheaper than either failure.
      const sweep = () => {
        const limit = window.innerHeight;
        pending = pending.filter((el) => {
          if (el.getBoundingClientRect().top >= limit) return true;
          el.classList.add('revealed');
          return false;
        });
        if (pending.length === 0) cleanup();
      };

      window.addEventListener('scroll', sweep, { passive: true });
      window.addEventListener('resize', sweep);
      cleanup = () => {
        window.removeEventListener('scroll', sweep);
        window.removeEventListener('resize', sweep);
      };
      sweep();
    }, 100);

    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [location]); // Re-run when location changes
}

function AppContent() {
  useScrollReveal();

  return (
    <>
      <GlobalSEO />
      <AnalyticsIntegration 
        googleAnalyticsId={process.env.REACT_APP_GA_ID}
        googleTagManagerId={process.env.REACT_APP_GTM_ID}
        enableCookieConsent={true}
      />
      <WebVitalsTracker />
      <SEOOptimizations />
      <PerformanceOptimizations />
      <ImageOptimization />
      <ResourceOptimization />
      <ConsoleErrorFix />
      <ImageProtection />
      <Navbar />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/places" element={<Places />} />
          <Route path="/places/:country" element={<PlaceDetail />} />
          <Route path="/checklist/:country" element={<Checklist />} />
          <Route path="/routes/:country" element={<RouteMap />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/about" element={<About />} />
          <Route path="/plan-trip" element={<PlanTrip />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/hotels" element={<Hotels />} />
          <Route path="/hotels/:hotelId" element={<HotelDetail />} />
          <Route path="/image-protection-test" element={<ImageProtectionTest />} />
          <Route path="/admin" element={<AdminErrorBoundary><Admin /></AdminErrorBoundary>} />
          <Route path="/admin/trips" element={<AdminErrorBoundary><AdminTrips /></AdminErrorBoundary>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <Safarbot />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App;
